import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { Tooltip as MuiTooltip } from '@mui/material'; // Alias to avoid naming conflict
import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MetricId } from '../../../common/graphql/generated/graphql-sdk';
import { MetricStore } from '../../../common/metrics/metric-store';
import {
  darkTeal,
  mediumGray,
  teal2,
} from '../../../common/theme/color-constants';
import { TimeDisplayFormat } from '../../../common/v2/types';

interface TimeFormatToggleIconProps {
  metricId: MetricId;
  metricStore: MetricStore;
  // PBS table passes this so an equal-width empty box is kept when a metric
  // isn't time-valued, so the measure dropdowns and info icons stay
  // column-aligned. The metric chart omits it (renders nothing instead).
  keepSpaceWhenUnavailable?: boolean;
}

// Shared box for the button and its empty-slot placeholder. MeasureHeaderIcons
// in BalanceSheetTable sizes the neighbouring info icon to the same box, so the
// two always share one centre line.
const BOX = '18px';

const Slot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${BOX};
  height: ${BOX};
`;

// Bare icon button: teal filled clock when HH:MM is the active format, grey
// outline clock otherwise.
const ToggleButton = styled.button<{ $isActive: boolean }>`
  appearance: none;
  border: none;
  margin: 0;
  padding: 0;
  background: none;
  font: inherit;
  width: ${BOX};
  height: ${BOX};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ $isActive }) => ($isActive ? teal2 : mediumGray)};
  transition: color 100ms ease;

  svg {
    display: block;
    width: 15px;
    height: 15px;
  }

  &:hover {
    color: ${({ $isActive }) => ($isActive ? teal2 : darkTeal)};
  }

  &:focus-visible {
    outline: none;
    color: ${darkTeal};
  }
`;

export const TimeFormatToggleIcon: React.FC<TimeFormatToggleIconProps> =
  observer(({ metricId, metricStore, keepSpaceWhenUnavailable = false }) => {
    const { t } = useTranslation();

    if (!metricStore.isTimeFormatToggleAvailable(metricId)) {
      return keepSpaceWhenUnavailable ? <Slot aria-hidden /> : null;
    }

    const isHHMM =
      metricStore.getTimeDisplayFormat(metricId) === TimeDisplayFormat.HH_MM;

    const tooltipTitle = isHHMM
      ? t('common:commonValues.tooltips.showAsDecimal')
      : t('common:commonValues.tooltips.showAsHHMM');

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      metricStore.toggleTimeDisplayFormat(metricId);
    };

    return (
      <Slot>
        <MuiTooltip title={tooltipTitle} placement="top">
          <ToggleButton
            type="button"
            $isActive={isHHMM}
            onClick={handleClick}
            aria-label={tooltipTitle}
            aria-pressed={isHHMM}
          >
            {isHHMM ? (
              <AccessTimeFilledRoundedIcon />
            ) : (
              <AccessTimeRoundedIcon />
            )}
          </ToggleButton>
        </MuiTooltip>
      </Slot>
    );
  });
